import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, filter, interval, map, Observable, switchMap, take, throwError, timeout } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { Workspace } from '../models/workspace.model';
import { convertKeysToCamelCase } from '../../../../../shared/utilities/json-key-transform';
import { GetWorkspaceListRequest } from '../models/get-workspace-list.request';
import { GetWorkspaceListResponse } from '../models/get-workspace-list.response';
import { WorkspaceFailureStates, WorkspaceProvisionStatusType, WorkspaceSuccessStates } from '../models/workspace-provision-status.model';
import { WorkspaceSignalRNotification } from '../../../../../core/models/signal-r-notification.model';
import { ToastService } from '../../../../../core/services/toast.service';
import { CreateWorkspaceJobRequest } from '../models/create-workspace-job.request';
import { CreateWorkspaceJobResponse } from '../models/create-workspace-job.response';
import { GetWorkspaceJobStatusRequest } from '../models/get-workspace-job-status.request';
import { GetWorkspaceJobStatusResponse } from '../models/get-workspace-job-status.response';
import { DisableWorkspacePublicNetworkRequest } from '../models/disable-workspace-public-network-request';
import { DisableWorkspacePublicNetworkJobResponse } from '../models/disable-workspace-public-network-job.response';
import { EditWorkspaceDialogComponent } from '../../../dialogs/edit-workspace-dialog/edit-workspace-dialog.component';
import { UpdateWorkspaceAliasRequest } from '../models/update-workspace-alias.request';
import { AddWorkspaceDialogComponent } from '../../../dialogs/add-workspace-dialog/add-workspace-dialog.component';
import { WorkspaceCreationStageType } from '../models/workspace-creation-stage-status.model';
import { UpdateWorkspaceAliasResponse } from '../models/update-workspace-alias.response';
import { WorkspacePublicNetworkAccessType } from '../models/workspace-public-network-access.model';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private http = inject(HttpClient);
  private apiUrl = environment.workspaceApi.serviceBaseUrl;

  private lastRequestedTenantId = signal<string | null>(null);

  toastService = inject(ToastService);

  workspaces = signal<Workspace[]>([]);

  isWorkspaceLoading = signal(false);
  workspaceErrorMessage = signal<string>("");

  getWorkspaceList(request: GetWorkspaceListRequest): void {
    this.isWorkspaceLoading.set(true);
    this.workspaces.set([]);
    this.workspaceErrorMessage.set("");
    this.lastRequestedTenantId.set(request.tenantId);

    this.http.post<GetWorkspaceListResponse>(this.apiUrl+"/GetWorkspaces", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as GetWorkspaceListResponse;
        return camelCase.workspaces;
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.status + ": " + error.statusText);
      })
    ).subscribe({
      next: (workspaces) => {
        // only update if this is still the latest requested tenant
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.workspaces.set(/*this.sortWorkspaces(*/workspaces/*)*/);
          this.isWorkspaceLoading.set(false);
        }
      },
      error: (error: string) => {
        if (request.tenantId === this.lastRequestedTenantId()) {
          this.workspaces.set([]);
          this.workspaceErrorMessage.set("Failed to get workspaces. Please try again later. (" + error + ")");
          this.isWorkspaceLoading.set(false);
        }
      }
    });
  }

  createWorkspace(request: CreateWorkspaceJobRequest): Observable<Workspace> {
    // create workspace job
    return this.http.post<CreateWorkspaceJobResponse>(this.apiUrl + "/CreateWorkspaceJob", request).pipe(
      switchMap((result) => {
        const camelCase = convertKeysToCamelCase(result) as CreateWorkspaceJobResponse;
        const jobId = camelCase.jobId;

        // poll jobs
        const jobStatusRequest: GetWorkspaceJobStatusRequest = { jobId }

        return interval(60_000).pipe(
          switchMap(() => 
            this.http.post<GetWorkspaceJobStatusResponse>(this.apiUrl + "/GetWorkspaceCreationJobStatus", jobStatusRequest)
          ),
          map((statusResult) => {
            const status = convertKeysToCamelCase(statusResult) as GetWorkspaceJobStatusResponse;

            if (status.status === "Failed") {
              throw new Error("Workspace creation failed. Please try again later");
            }

            if (status.status === "Succeeded" && status.workspaceName !== null && status.databricksWorkspaceId !== null) {
              const workspace: Workspace = {
                workspaceName: status.workspaceName!,
                workspaceAlias: status.workspaceAlias!,
                databricksWorkspaceId: status.databricksWorkspaceId!,
                workspaceId: status.workspaceId!,
                provisionStatus: WorkspaceProvisionStatusType.Succeeded,
                createdDateTime: this.parseUtc(status.createdAt!),
                publicNetworkAccess: status.publicNetworkAccess!,
              };
              return workspace
            }

            return null;
          }),
          filter(Boolean), // filter to non null workspaces
          take(1), // only complete after first success
          timeout({
            each: 20 * 60_000, // 20 minutes timeout
            with: () => throwError(() => new Error("Workspace creation timed out after 20 minutes"))
          }),
          catchError((error) => {
            console.log(error)
            if (error instanceof HttpErrorResponse) {
              return throwError(() => error.error?.Error || error.message);
            } else if (error instanceof Error) {
              return throwError(() => error.message);
            } else {
              return throwError(() => "Unkown error occured");
            }
          })
        );
      }),
      catchError((error) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.error.error);
      })
    );
  }

  disableWorkspacePublicNetwork(request: DisableWorkspacePublicNetworkRequest): Observable<string> {
    return this.http.post<DisableWorkspacePublicNetworkJobResponse>(this.apiUrl + "/DisableWorkspacePublicNetwork", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as DisableWorkspacePublicNetworkJobResponse;
        return camelCase.jobId
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.error.error);
      })
    );
  }

  updateWorkspaceAlias(request: UpdateWorkspaceAliasRequest): Observable<string> {
    return this.http.post<UpdateWorkspaceAliasResponse>(this.apiUrl + "/UpdateWorkspaceAlias", request).pipe(
      map(response => {
        const camelCase = convertKeysToCamelCase(response) as UpdateWorkspaceAliasResponse;
        return camelCase.workspaceAlias
      }),
      catchError((error: HttpErrorResponse) => {
        error = convertKeysToCamelCase(error);
        return throwError(() => error.error?.error || error.message);
      })
    );
  }

  handleWorkspaceUpdate(notification: WorkspaceSignalRNotification, currentTenantId: string | undefined): void {
    if (currentTenantId) {
      if (WorkspaceSuccessStates.includes(notification.data.provisioningState)) {
        AddWorkspaceDialogComponent.workspaceCreationStage$.next({
          tenantId: notification.UIStateParams.tenantId,
          creationStage: WorkspaceCreationStageType.Configuring
        });
      }
      EditWorkspaceDialogComponent.closeDialog$.next({
        workspaceName: notification.UIStateParams.workspaceName,
        tenantId: notification.UIStateParams.tenantId
      });
    }

    const currentWorkspaces = this.workspaces();
    if (!currentWorkspaces) return;

    const workspaceIndex = currentWorkspaces.findIndex(workspace => workspace.workspaceName === notification.UIStateParams.workspaceName);

    if (workspaceIndex !== -1) {
      currentWorkspaces[workspaceIndex].provisionStatus = notification.data.provisioningState;
      currentWorkspaces[workspaceIndex].publicNetworkAccess = notification.data.publicNetworkAccess;
    }

    this.workspaces.set(currentWorkspaces);

    // only show toast for success or failure, not for creating
    if (WorkspaceSuccessStates.includes(notification.data.provisioningState)) {
      this.toastService.showSuccess(notification.message);
    } else if (WorkspaceFailureStates.includes(notification.data.provisioningState)) {
      this.toastService.showError(notification.message);
    }
  }

  private parseUtc(dateStr: string): Date {
    // append Z to string, tells the date pipeline that it is UTC as without it, doesn't know what timezone the date is for
    return new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
  }
}
